import React from 'react';
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { ArrowRight, Database, Brain, Zap, DollarSign, Server, Network } from "lucide-react";
import RouterDemo from './RouterDemo';

const HowItWorks: React.FC = () => {
    return (
        <section className="py-12 md:py-16 relative overflow-hidden">
            <div className="absolute inset-0 bg-grid-slate-900/[0.04] bg-[bottom_1px_center] dark:bg-grid-slate-400/[0.05] [mask-image:linear-gradient(to_bottom,transparent,black,transparent)]" />

            <div className="container mx-auto px-4 relative">
                <div className="text-center max-w-3xl mx-auto mb-10 md:mb-12">
                    <Badge variant="outline" className="mb-4 border-emerald-500/20 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                        Architecture
                    </Badge>
                    <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
                        How SigLLM Works
                    </h2>
                    <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
                        Intelligent routing that slashes costs without sacrificing quality.
                    </p>
                </div>

                <div className="relative max-w-7xl mx-auto">
                    {/* Desktop Layout - Visual Flow */}
                    <div className="hidden xl:grid grid-cols-[2fr_auto_3fr_auto_3fr_auto_2fr] gap-4 items-stretch">

                        {/* 1. Input */}
                        <div className="flex flex-col justify-center items-center z-10">
                            <Card className="p-6 w-full text-center border-primary/20 shadow-lg shadow-primary/5 h-auto min-h-[140px] flex flex-col justify-center items-center bg-background/80 backdrop-blur-sm transition-transform hover:scale-105 duration-300">
                                <span className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-2">Input</span>
                                <div className="text-lg font-bold text-foreground">API Request</div>
                            </Card>
                        </div>

                        <div className="flex items-center justify-center z-0">
                            <ArrowRight className="text-muted-foreground/30 h-8 w-8" />
                        </div>

                        {/* 2. Engine Core */}
                        <div className="flex flex-col justify-center z-10">
                            <div className="w-full relative group h-full">
                                <div className="absolute -inset-0.5 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-2xl blur opacity-20 group-hover:opacity-40 transition duration-1000"></div>
                                <Card className="relative p-6 border-emerald-500/30 bg-background/90 backdrop-blur-xl h-full min-h-[280px] flex flex-col justify-between hover:border-emerald-500/50 transition-colors">
                                    <div className="flex items-center gap-4 mb-6">
                                        <div className="h-12 w-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shrink-0">
                                            <Brain className="h-7 w-7" />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-xl">SigLLM Engine</h3>
                                            <p className="text-sm text-muted-foreground">Orchestrator</p>
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between text-sm p-3 rounded-lg bg-muted/50 border border-border/50">
                                            <div className="flex items-center gap-3">
                                                <Database className="h-4 w-4 text-purple-500" />
                                                <span className="font-medium">Semantic Cache</span>
                                            </div>
                                            <Badge variant="secondary" className="text-[10px] px-2 py-0.5 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 font-bold">-100% Cost</Badge>
                                        </div>
                                        <div className="flex items-center justify-between text-sm p-3 rounded-lg bg-muted/50 border border-border/50">
                                            <div className="flex items-center gap-3">
                                                <Network className="h-4 w-4 text-blue-500" />
                                                <span className="font-medium">AI Router</span>
                                            </div>
                                            <Badge variant="secondary" className="text-[10px] px-2 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 font-bold">Smart Logic</Badge>
                                        </div>
                                    </div>
                                </Card>
                            </div>
                        </div>

                        {/* Arrows Split */}
                        <div className="h-full flex flex-col justify-center items-center relative z-0 w-16 mx-auto">
                            <div className="absolute left-0 top-1/2 -translate-y-1/2 flex flex-col items-center h-[140px] justify-between w-full">
                                <div className="w-full border-t-2 border-dashed border-emerald-400/50 relative">
                                    <ArrowRight className="absolute -right-2 -top-3 h-6 w-6 text-emerald-500 fill-emerald-500" />
                                </div>
                                <div className="w-full border-t-2 border-dashed border-orange-400/50 relative">
                                    <ArrowRight className="absolute -right-2 -top-3 h-6 w-6 text-orange-500 fill-orange-500" />
                                </div>
                            </div>
                        </div>

                        {/* 3. Models */}
                        <div className="flex flex-col gap-6 justify-center z-10">
                            {/* Weak Route */}
                            <Card className="p-5 border-emerald-500/20 bg-emerald-50/30 hover:bg-emerald-50/50 transition-colors cursor-default group">
                                <div className="flex justify-between items-center mb-3">
                                    <Badge variant="outline" className="border-emerald-200 text-emerald-700 bg-white/50 group-hover:bg-emerald-100/50 transition-colors">
                                        Simple Query
                                    </Badge>
                                    <span className="text-xs font-mono text-emerald-600 font-bold">-80% Token Cost</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="p-2 rounded-md bg-white/60 text-emerald-600 shadow-sm">
                                        <Zap className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <div className="font-semibold text-base">Low cost LLM</div>
                                        <div className="text-xs text-muted-foreground">GPT-5.2 Nano, GPT-5.2 Flash</div>
                                    </div>
                                </div>
                            </Card>

                            {/* Strong Route */}
                            <Card className="p-5 border-orange-500/20 bg-orange-50/30 hover:bg-orange-50/50 transition-colors cursor-default group">
                                <div className="flex justify-between items-center mb-3">
                                    <Badge variant="outline" className="border-orange-200 text-orange-700 bg-white/50 group-hover:bg-orange-100/50 transition-colors">
                                        Complex Query
                                    </Badge>
                                    <span className="text-xs font-mono text-orange-600 font-bold">Standard Cost</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="p-2 rounded-md bg-white/60 text-orange-600 shadow-sm">
                                        <Brain className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <div className="font-semibold text-base">Full weighted LLM</div>
                                        <div className="text-xs text-muted-foreground">GPT-5, GPT-5.2 Pro, Gemini 3 Pro</div>
                                    </div>
                                </div>
                            </Card>
                        </div>

                        <div className="flex items-center justify-center z-0">
                            <ArrowRight className="text-muted-foreground/30 h-8 w-8" />
                        </div>

                        {/* 4. Output */}
                        <div className="flex flex-col justify-center items-center z-10">
                            <Card className="relative p-6 w-full text-center border-emerald-500/30 shadow-[0_0_30px_-10px_rgba(16,185,129,0.15)] h-auto min-h-[140px] flex flex-col justify-center items-center bg-emerald-50/10 backdrop-blur-sm transition-transform hover:scale-105 duration-300">
                                <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-[10px] font-bold text-emerald-600 dark:text-emerald-400">
                                    - 90%
                                </div>
                                <div className="flex items-center justify-center gap-1.5 mb-2 text-emerald-600">
                                    <span className="text-xs font-bold uppercase tracking-widest">Optimized response</span>
                                </div>
                                <div className="text-lg font-bold text-foreground leading-tight">90% lower token cost</div>
                            </Card>
                        </div>
                    </div>

                    {/* Tablet/Mobile Layout - Adaptive Stack */}
                    <div className="xl:hidden flex flex-col items-center max-w-2xl mx-auto space-y-8">
                        {/* Phase 1: Input */}
                        <div className="w-full md:max-w-xs relative">
                            <div className="hidden md:block absolute -right-12 top-1/2 -translate-y-1/2 text-muted-foreground/30">
                                <ArrowRight className="h-8 w-8 rotate-90 md:rotate-0" />
                            </div>
                            <Card className="p-6 w-full text-center shadow-sm border-primary/20">
                                <div className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-1">Input</div>
                                <div className="font-bold text-xl">API Request</div>
                            </Card>
                            <div className="md:hidden flex justify-center mt-4 text-muted-foreground/30">
                                <ArrowRight className="h-6 w-6 rotate-90" />
                            </div>
                        </div>

                        {/* Phase 2: Engine */}
                        <div className="w-full relative group">
                            <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-2xl blur opacity-20"></div>
                            <Card className="relative p-6 md:p-8 w-full border-emerald-500/30 bg-background/95">
                                <div className="flex flex-col md:flex-row items-center gap-6 mb-8">
                                    <div className="h-16 w-16 rounded-2xl bg-emerald-100 flex items-center justify-center text-emerald-600 shrink-0">
                                        <Brain className="h-8 w-8" />
                                    </div>
                                    <div className="text-center md:text-left">
                                        <h3 className="font-bold text-2xl">SigLLM Engine</h3>
                                        <p className="text-muted-foreground">Intelligent Orchestration Layer</p>
                                    </div>
                                </div>

                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="flex items-center justify-between p-4 bg-muted/30 rounded-xl border border-border/50">
                                        <div className="flex items-center gap-3">
                                            <Database className="h-5 w-5 text-purple-500" />
                                            <span className="font-semibold">Semantic Cache</span>
                                        </div>
                                        <Badge variant="secondary" className="text-purple-600 bg-purple-50 font-bold">-100% Cost</Badge>
                                    </div>
                                    <div className="flex items-center justify-between p-4 bg-muted/30 rounded-xl border border-border/50">
                                        <div className="flex items-center gap-3">
                                            <Network className="h-5 w-5 text-blue-500" />
                                            <span className="font-semibold">AI Router</span>
                                        </div>
                                        <Badge variant="secondary" className="text-blue-600 bg-blue-50 font-bold">Smart Logic</Badge>
                                    </div>
                                </div>
                            </Card>
                            <div className="flex justify-center mt-8 text-muted-foreground/30">
                                <ArrowRight className="h-6 w-6 rotate-90" />
                            </div>
                        </div>

                        {/* Phase 3: Models Split */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
                            <Card className="p-6 border-emerald-500/20 bg-emerald-50/20 flex flex-col items-center text-center gap-3 hover:bg-emerald-50/40 transition-colors">
                                <Badge variant="outline" className="border-emerald-200 bg-white/60 text-emerald-700">Simple Query</Badge>
                                <div className="p-3 bg-white/60 rounded-full text-emerald-600 mb-1">
                                    <Zap className="h-6 w-6" />
                                </div>
                                <div>
                                    <div className="font-bold text-lg">Low-weights LLM</div>
                                    <div className="text-sm text-muted-foreground">GPT-5.2 Nano, GPT-5.2 Flash</div>
                                </div>
                                <div className="text-sm font-bold text-emerald-600 mt-2 px-3 py-1 bg-emerald-100/50 rounded-full">80% Token Costs Saved</div>
                            </Card>

                            <Card className="p-6 border-orange-500/20 bg-orange-50/20 flex flex-col items-center text-center gap-3 hover:bg-orange-50/40 transition-colors">
                                <Badge variant="outline" className="border-orange-200 bg-white/60 text-orange-700">Complex Query</Badge>
                                <div className="p-3 bg-white/60 rounded-full text-orange-600 mb-1">
                                    <Brain className="h-6 w-6" />
                                </div>
                                <div>
                                    <div className="font-bold text-lg">Strong LLM</div>
                                    <div className="text-sm text-muted-foreground">GPT-5.2, GPT-5.2 Pro</div>
                                </div>
                                <div className="text-sm font-bold text-orange-600 mt-2 px-3 py-1 bg-orange-100/50 rounded-full">Standard Cost</div>
                            </Card>
                        </div>

                        <div className="flex justify-center text-muted-foreground/30">
                            <ArrowRight className="h-6 w-6 rotate-90" />
                        </div>

                        {/* Phase 4: Result */}
                        <Card className="relative p-8 w-full md:max-w-md text-center border-emerald-500/30 shadow-2xl shadow-emerald-500/10 bg-gradient-to-b from-background to-emerald-50/10">
                            <div className="absolute top-3 right-3 px-3 py-1 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-xs font-bold text-emerald-600 dark:text-emerald-400">
                                - 80% Cost
                            </div>
                            <div className="flex items-center justify-center gap-2 text-emerald-600 mb-2">
                                <span className="text-sm font-bold uppercase tracking-widest">Result</span>
                            </div>
                            <div className="font-bold text-2xl text-foreground">Optimized Response</div>
                            <p className="mt-2 text-muted-foreground">High quality, lowest price.</p>
                        </Card>
                    </div>
                </div>
            </div>

            <div id="demo" className="container mx-auto px-4 mt-14 relative">
                <div className="text-center max-w-2xl mx-auto mb-6">
                    <p className="text-base leading-7 text-muted-foreground md:text-lg">
                        See how the <strong>SigLLM Engine</strong> can slash support chatbot costs. Send a query and watch the AI dynamically select the most cost-effective path in real-time.
                    </p>
                </div>
                <div className="mx-auto max-w-4xl">
                    <RouterDemo />
                </div>
            </div>
        </section>
    );
};

export default HowItWorks;
